import React, { createRef } from "react";
import { Form } from "react-bootstrap";

import MyTable from "components/MyTable";
import SelectRows from "components/SelectRows";
import confirm from "components/Confirm";
import Toast from "services/Toast";
import URLVerificationService from "services/URLVerificationService";
import {
    TABLE_COLUMNS,
    BULK_ACTIONS,
    DEFAULT_SORT,
    RENDER_COLUMNS,
    STATUS_OPTIONS,
    filterAndSort,
} from "./URLVerificationConfig";
import Emitter from "services/Emitter";

class URLVerificationTab extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = createRef();
        this.state = {
            selection: "none",
            selected: 0,
        };
        this.changeSelection = this.changeSelection.bind(this);
        this.withSelected = this.withSelected.bind(this);
        this.loadData = this.loadData.bind(this);
        this.changeStatus = this.changeStatus.bind(this);
    }

    componentDidMount() {
        Emitter.on(this.props.exportEvent, () => this.tableRef.current.CSV());
        this.tableRef.current.init();
    }

    componentWillUnmount() {
        Emitter.off(this.props.exportEvent);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            this.tableRef.current?.reload();
        }
    }

    async changeStatus(rowId, newStatus) {
        const statusToSend = newStatus || "unset";
        this.tableRef.current.setLoading(true);
        const result = await URLVerificationService.updateStatus(
            [rowId],
            statusToSend
        );
        if (!result) {
            Toast.error("Failed to update status");
            this.tableRef.current.setLoading(false);
            return;
        }
        Toast.ok(`Status updated to ${statusToSend}`);
        this.props.onRefresh?.();
    }

    loadData(setup) {
        return new Promise((resolve) => {
            const { total, items } = this.props.data || { total: 0, items: [] };
            Emitter.emit(this.props.countEvent, total || 0);
            const result = filterAndSort(items || [], setup);
            this.setState({ selection: "none", selected: 0 }, () =>
                resolve(result)
            );
        });
    }

    changeSelection(evt) {
        const data = this.tableRef.current.data(false);

        if (evt === "all") {
            data.forEach((row) => {
                row.selected = true;
                this.tableRef.current.setRow(row.index, row);
            });
            this.setState({ selection: "all", selected: data.length });
            return;
        }

        if (evt === "none") {
            data.forEach((row) => {
                delete row.selected;
                this.tableRef.current.setRow(row.index, row);
            });
            this.setState({ selection: "none", selected: 0 });
            return;
        }

        if (evt === "invert") {
            let selected = 0;
            data.forEach((row) => {
                if (
                    typeof row.selected === "undefined" ||
                    row.selected === false
                ) {
                    row.selected = true;
                    selected++;
                } else {
                    delete row.selected;
                }
                this.tableRef.current.setRow(row.index, row);
            });
            this.setState({ selected });
            return;
        }

        const obj = this.tableRef.current.getRowFromEvent(evt);
        const checked = evt.target.checked;
        const checkedRows =
            data.filter((row) => row.selected).length + (checked ? 1 : -1);
        const partial = checkedRows && checkedRows < data.length;
        obj.row.selected = checked;
        this.tableRef.current.setRow(obj.index, obj.row);
        this.setState({
            selection: partial ? "partial" : checked ? "all" : "none",
            selected: checkedRows,
        });
    }

    async withSelected(action) {
        if (
            !(await confirm("Are you sure?", "Yes", "No", {
                message: `Are you sure you want to ${action} the selected items?`,
            }))
        ) {
            return;
        }

        this.tableRef.current.setLoading(true);
        const ids = this.tableRef.current
            .data(false)
            .filter((row) => row.selected)
            .map((row) => row.id);

        URLVerificationService.updateStatus(ids, action).then((result) => {
            if (result) {
                Toast.ok("Action performed successfully");
                this.props.onRefresh?.();
                return;
            }

            Toast.error("Something went wrong while performing the action");
            this.tableRef.current.setLoading(false);
        });
    }

    render() {
        return (
            <div className="pt-4">
                <MyTable
                    name={this.props.name}
                    ref={this.tableRef}
                    dynamic={true}
                    sortBy={DEFAULT_SORT.field}
                    sortOrder={DEFAULT_SORT.order}
                    headers={[
                        {
                            field: "actions",
                            label: "Bulk",
                            content: (
                                <SelectRows
                                    state={this.state.selection}
                                    onSelect={this.changeSelection}
                                    actions={[
                                        {
                                            label: "Select Status",
                                            onClick: () =>
                                                this.withSelected(
                                                    BULK_ACTIONS.UNSET
                                                ),
                                        },
                                        {
                                            label: "Approve Selected",
                                            onClick: () =>
                                                this.withSelected(
                                                    BULK_ACTIONS.APPROVE
                                                ),
                                        },
                                        {
                                            label: "Reject Selected",
                                            onClick: () =>
                                                this.withSelected(
                                                    BULK_ACTIONS.REJECT
                                                ),
                                        },
                                    ]}
                                />
                            ),
                        },
                        ...TABLE_COLUMNS,
                    ]}
                    reload={this.loadData}
                    renderColumns={{
                        ...RENDER_COLUMNS,
                        actions: (row) => (
                            <Form.Check
                                className="lg"
                                checked={row.selected ?? false}
                                onClick={this.changeSelection}
                            />
                        ),
                        status: (row) => (
                            <Form.Select
                                size="sm"
                                value={row.status === "unset" ? "" : row.status}
                                onChange={(e) =>
                                    this.changeStatus(row.id, e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                        ),
                    }}
                />
            </div>
        );
    }
}

export default URLVerificationTab;
